import { useEffect, useRef, useState, type CSSProperties } from 'react';
import ranade400 from '../../assets/al-one-ranade-400.woff2';
import ranade700 from '../../assets/al-one-ranade-700.woff2';

/** AL-ONE layer stack graphic (from al-one.ai). Tile labels are sample data — decorative only. */
const TILES = [
  { kind: 'pms', label: 'Rent roll' },
  { kind: 'pms', label: 'T12' },
  { kind: 'pms', label: 'Budget' },
  { kind: 'pms', label: 'Lease trade-outs' },
  { kind: 'pms', label: 'Concessions' },
  { kind: 'pms', label: 'Delinquency' },
  { kind: 'mkt', label: 'Market data' },
  { kind: 'doc', label: 'Loan documents' },
  { kind: 'doc', label: 'Fund documents' },
  { kind: 'doc', label: 'Internal rankings' },
  { kind: 'yours', label: '+ Your data source' },
  { kind: 'top', label: 'Juniper Flats, the report' },
] as const;

const LEGEND = [
  { kind: 'pms', label: 'Our PMS' },
  { kind: 'mkt', label: 'The Market' },
  { kind: 'doc', label: 'Our Documents' },
  { kind: 'yours', label: 'Our Data Sources' },
] as const;

const STYLES = `
@font-face { font-family:'AL1 Ranade'; font-weight:400; font-style:normal; font-display:swap; src:url(${ranade400}) format('woff2'); }
@font-face { font-family:'AL1 Ranade'; font-weight:700; font-style:normal; font-display:swap; src:url(${ranade700}) format('woff2'); }
.al1-stack { --al1-ink2:#2e4257; --al1-muted:#5d6b7a; --al1-slate:#415a77; display:flex; flex-direction:column; align-items:center; font:16px/1.55 'AL1 Ranade',system-ui,-apple-system,'Segoe UI',sans-serif; -webkit-font-smoothing:antialiased; padding-top:48px; overflow-x:clip; overflow-y:visible; }
.al1-stack .al1-stage { height:520px; width:100%; max-width:720px; perspective:1100px; display:grid; place-items:center; }
.al1-stack .al1-pile { position:relative; width:216px; height:216px; margin-top:150px; transform-style:preserve-3d; transform:rotateX(58deg) rotateZ(-38deg); }
.al1-stack .al1-tile { position:absolute; inset:0; box-sizing:border-box; border-radius:22px; border:1px solid rgba(13,27,42,.12); background:linear-gradient(135deg,#fff,#edf1f5); transform:translateZ(calc(var(--n)*4px)); transition:transform 1.6s cubic-bezier(.2,.7,.1,1),box-shadow 1.6s; display:flex; align-items:flex-end; padding:16px; box-shadow:0 20px 40px -22px rgba(13,27,42,.4); }
.al1-stack .al1-tile span { font:700 12.5px/1.2 'AL1 Ranade',sans-serif; color:var(--al1-ink2); opacity:0; transition:opacity .8s ease calc(.6s + var(--n)*.08s); }
.al1-stack .al1-mkt { background:linear-gradient(135deg,#e7eef5,#c9d6e3); }
.al1-stack .al1-doc { background:linear-gradient(135deg,#5d7796,#2e4257); border-color:rgba(13,27,42,.35); } .al1-stack .al1-doc span { color:#fff; }
.al1-stack .al1-yours { background:rgba(255,255,255,.6); border:1.5px dashed #5d7796; box-shadow:none; } .al1-stack .al1-yours span { color:var(--al1-slate); }
.al1-stack .al1-top { background:linear-gradient(135deg,#de6a4f,#cc361e); border-color:rgba(204,54,30,.5); } .al1-stack .al1-top span { color:#fff; }
.al1-stack .al1-pile.al1-go .al1-tile { transform:translateZ(calc(var(--n)*var(--al1-step,30px))); }
.al1-stack .al1-pile.al1-go .al1-tile span { opacity:1; }
.al1-stack .al1-pile.al1-go .al1-top { box-shadow:0 0 60px rgba(222,106,79,.45),0 20px 40px -10px rgba(13,27,42,.3); }
.al1-stack .al1-legend { display:flex; flex-wrap:wrap; justify-content:center; gap:6px 16px; font-size:12.5px; color:var(--al1-muted); margin-top:4px; padding:0 16px; }
.al1-stack .al1-lg { display:inline-block; box-sizing:border-box; width:12px; height:12px; border-radius:3px; margin-right:6px; vertical-align:-2px; border:1px solid rgba(13,27,42,.15); }
.al1-stack .al1-lg.al1-pms { background:#fff; } .al1-stack .al1-lg.al1-mkt { background:#c9d6e3; } .al1-stack .al1-lg.al1-doc { background:#2e4257; } .al1-stack .al1-lg.al1-yours { background:#fff; border:1.5px dashed #5d7796; }
@media (max-width:640px) { .al1-stack .al1-pile { --al1-step:21px; transform:rotateX(58deg) rotateZ(-38deg) scale(.86); } .al1-stack .al1-stage { height:420px; } }
@media (prefers-reduced-motion: reduce) { .al1-stack .al1-tile, .al1-stack .al1-tile span { transition:none !important; } }
`;

export function AlOneLayerStack({ className = '' }: { className?: string }) {
  const pileRef = useRef<HTMLDivElement>(null);
  const [spread, setSpread] = useState(false);

  useEffect(() => {
    const el = pileRef.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      setSpread(true);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          timer = setTimeout(() => setSpread(true), 250);
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearTimeout(timer);
    };
  }, []);

  return (
    <div
      className={`al1-stack ${className}`}
      role="img"
      aria-label="AL-ONE builds one report from many sources: the property's rent roll, T12, budget, lease trade-outs, concessions and delinquency from the property management system, market data, and the firm's own loan documents, fund documents and internal rankings, with room for your own data source."
    >
      <style>{STYLES}</style>
      <div className="al1-stage">
        <div ref={pileRef} className={`al1-pile${spread ? ' al1-go' : ''}`}>
          {TILES.map((tile, n) => (
            <div key={tile.label} className={`al1-tile al1-${tile.kind}`} style={{ '--n': n } as CSSProperties}>
              <span>{tile.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="al1-legend">
        {LEGEND.map((item) => (
          <span key={item.kind}>
            <i className={`al1-lg al1-${item.kind}`} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}
